import type { ContentDocument } from "../../domain/content-schema";

export const sepsisEarlyRecognitionCard: ContentDocument = {
  identity: {
    id: "klinik-kartlar.cocukta-sepsisin-erken-taninmasi",
    category: "clinical_cards",
    slug: "cocukta-sepsisin-erken-taninmasi",
    title: "Çocukta Sepsisin Erken Tanınması",
    shortTitle: "Sepsisi erken tanı",
    summary: "Enfeksiyon olasılığı bulunan çocukta yeni organ işlev bozukluğunu davranış, solunum, perfüzyon, deri görünümü ve idrar çıkışındaki değişikliklerle erken fark edin.",
    kind: "clinical_card",
    tags: ["sepsis", "septik şok", "erken tanı", "seri değerlendirme"],
    accessTier: "public",
  },
  review: {
    authors: ["PedEmUP Editör Kurulu"],
    reviewers: [],
    reviewedAt: "2026-08-30",
    nextReviewAt: "2027-08-30",
    contentVersion: "1.0.0-pilot.1",
    clinicalVersion: "2026.08-source-snapshot",
    editorialStatus: "in_review",
    clinicalReviewStatus: "pending",
    evidenceLabel: "PedEmUP 30 Ağustos 2026 kaynak görüntüsü; native pilot dönüşümü",
  },
  canonicalConcepts: [
    { id: "INF-001", kind: "diagnosis", owner: "pemguide-clinical", status: "candidate" },
  ],
  relations: [
    { relation: "open-opeg", destinationTargetId: "clinical:opeg:INF-001", status: "draft", ownerGate: "clinical-deep-link-contract" },
    { relation: "review-guideline", destinationTargetId: "clinical:guideline:INF-001", status: "draft", ownerGate: "clinical-deep-link-contract" },
  ],
  learningObjectives: [
    "Hipotansiyon gelişmeden önceki organ işlev bozukluğu belirtilerini tanımak",
    "İlk değerlendirmeyi zaman duyarlı ve paralel biçimde yürütmek",
    "Tek ölçümün oluşturabileceği yanlış güveni önlemek",
  ],
  blocks: [
    { type: "heading", level: 2, text: "30 saniyede tanıma çekirdeği", anchor: "otuz-saniye" },
    { type: "clinical_pearl", title: "Enfeksiyon kuşkusu + organ işlev bozukluğu", body: "Normal kan basıncı kompanse dolaşım bozukluğunu dışlamaz. Klinik seyri ve birden fazla organ alanındaki değişimi birlikte değerlendirin." },
    { type: "clinical_table", caption: "İlk taramada değerlendirilecek altı alan", columns: ["Alan", "Aranacak değişiklik"], mobileMode: "cards", rows: [
      ["Davranış ve bilinç", "Aileye göre belirgin değişiklik, letarji, zor uyandırılma, irritabilite veya bilinçte gerileme."],
      ["Solunum", "Yaşa göre taşipne veya apne, artmış solunum işi, yeni oksijen gereksinimi."],
      ["Dolaşım ve perfüzyon", "Açıklanamayan taşikardi, uzamış kapiller geri dolum, zayıf nabız ve soğuk ekstremiteler."],
      ["Deri ve görünüm", "Soluk, gri, alacalı veya siyanotik görünüm ve basınçla solmayan döküntü."],
      ["Böbrek ve sıvı dengesi", "İdrar çıkışında azalma, azalmış oral alım veya belirgin sıvı kaybı."],
      ["Enfeksiyon ve konak", "Ateş ya da hipotermi ile bağışıklık baskılanması, onkoloji, aspleni veya invaziv araç gibi riskler."],
    ] },
    { type: "heading", level: 2, text: "İlk 15 dakika", anchor: "ilk-on-bes-dakika" },
    { type: "list", style: "ordered", items: [
      "Hava yolu, solunum, dolaşım, bilinç ve deri görünümünü birlikte değerlendirin.",
      "Sürekli fizyolojik izlem başlatın ve kan glukozunu ölçün.",
      "Enfeksiyon odağını, konak risklerini ve aile gözlemini kaydedin.",
      "Şok veya hızlı kötüleşme varsa kıdemli hekim ve çocuk yoğun bakım desteğini erken etkinleştirin.",
      "Damar yolu, gerekli örnekler ve zaman duyarlı tedaviyi birbirini gereksiz yere bekletmeden paralel yürütün.",
    ] },
    { type: "safety_box", severity: "critical", title: "Tedaviyi bir skoru tamamlamak için geciktirmeyin", body: [
      { type: "paragraph", text: "Phoenix ölçütleri sepsisi tanımlamak için geliştirilmiştir; erken klinik taramanın veya zaman duyarlı tedavi kararının yerine geçmez." },
    ] },
    { type: "heading", level: 2, text: "Yeniden değerlendirme ve yanlış güven", anchor: "yeniden-degerlendirme" },
    { type: "list", style: "unordered", items: [
      "Normal kan basıncı kompanse şoku dışlamaz.",
      "Tek bir normal laktat sepsisi veya yetersiz organ perfüzyonunu dışlamaz.",
      "Normal periferik oksijen satürasyonu dolaşım yeterliliğini göstermez.",
      "Ateş bulunmaması özellikle küçük bebekte ve bağışıklığı baskılanmış çocukta sepsisi dışlamaz.",
      "Tek bir normal değerlendirme seri muayenenin yerine geçmez.",
    ] },
    { type: "evidence_box", title: "Pilotun klinik sınırı", body: "Bu native pilot mevcut PedEmUP metnini yapılandırır. INF-001 eşlemesi klinik sahibi tarafından yayımlanmış bir OPEG veya Guideline deep-link sözleşmesi değildir; ilişkiler bu nedenle taslak kalır.", referenceIds: ["ssc-2026", "phoenix-consensus"] },
    { type: "heading", level: 2, text: "Kaynaklar ve sürüm", anchor: "kaynaklar" },
    { type: "references", referenceIds: ["ssc-2026", "phoenix-consensus", "nice-ng254"] },
  ],
  references: [
    { id: "ssc-2026", citation: "Weiss SL, Peters MJ, Oczkowski SJ, et al. Surviving Sepsis Campaign international guidelines for the management of sepsis and septic shock in children. 2026.", doi: "10.1097/PCC.0000000000003927", url: "https://doi.org/10.1097/PCC.0000000000003927" },
    { id: "phoenix-consensus", citation: "Schlapbach LJ, Watson RS, Sorce LR, et al. International Consensus Criteria for Pediatric Sepsis and Septic Shock. JAMA. 2024;331(8):665–674.", doi: "10.1001/jama.2024.0179", url: "https://doi.org/10.1001/jama.2024.0179" },
    { id: "nice-ng254", citation: "National Institute for Health and Care Excellence. Suspected sepsis in under 16s: recognition, diagnosis and early management. NG254. 2025.", url: "https://www.nice.org.uk/guidance/ng254" },
  ],
  relatedContentIds: [
    "vaka-ile-ogren.atesli-cocuk-sessiz-sok-sepsis",
    "hata-atlasi.pediatrik-sepsiste-tek-laktat-olcumu-yanilgisi",
    "makale-radar.cocuk-acil-sepsis-risk-ongorusu",
  ],
};
